// The home page's tiles, with the approved design's pictures and copy (TOM-980).
// The copy is the designer's, as approved. Each picture is a line drawing in
// the tile's line colour with one accent part (.home-pic__accent).
import type { ReactNode } from 'react';

export interface TileCopy {
  id: string;
  kicker?: string;
  title: string;
  /** The text the tile turns to. */
  text: string;
  /** An example question, shown under the text in the accent colour. */
  ask?: string;
  picture: ReactNode;
}

function Picture({ children }: { children: ReactNode }) {
  return (
    <svg className="home-pic" viewBox="0 0 200 140" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

export const MEET_NEO_TILES: readonly TileCopy[] = [
  {
    id: 'monitor',
    kicker: '01',
    title: 'Monitoring & diagnostics',
    text: 'Neo learns what normal looks like on your boat, spots unusual behavior early, and explains how serious it is — aboard or remotely.',
    ask: '“I just got a battery alert. What triggered it?”',
    picture: (
      <Picture>
        <path d="M40 92 A60 60 0 0 1 160 92" />
        <path d="M48 66 L56 70M70 44 L75 51M100 32 V41M130 44 L125 51M152 66 L144 70" />
        <path className="home-pic__accent home-pic__accent--thick" d="M100 92 L132 54" />
        <circle className="home-pic__solid" cx="100" cy="92" r="6" />
        <path d="M18 122 H70 L80 108 L92 134 L102 114 H182" />
      </Picture>
    ),
  },
  {
    id: 'talk',
    kicker: '02',
    title: 'Conversational assistance',
    text: 'Anyone aboard can ask in plain language and get clear, contextual answers — no menus, no manuals, no guesswork.',
    ask: '“Hey Neo, are the batteries charging properly?”',
    picture: (
      <Picture>
        <rect x="28" y="20" width="144" height="84" rx="18" />
        <path d="M58 104 L50 124 L80 104" />
        <path d="M54 57V71M65 50V78M76 43V85M87 54V74" />
        <path className="home-pic__accent home-pic__accent--mid" d="M98 40V88M109 49V79" />
        <path d="M120 55V73M131 46V82M142 53V75" />
      </Picture>
    ),
  },
  {
    id: 'memory',
    kicker: '03',
    title: 'Vessel-specific memory',
    text: 'Manuals, maintenance records, history and past conversations come together, so Neo knows your boat better every voyage.',
    ask: '“Remember that 65-foot boat near us in Red Bluff Bay last summer?”',
    picture: (
      <Picture>
        <path d="M100 40 C80 28 50 28 30 34 V116 C50 110 80 110 100 122 C120 110 150 110 170 116 V34 C150 28 120 28 100 40 Z" />
        <path d="M100 40 V122" />
        <path d="M44 56 H86 M44 70 H80 M44 84 H84" />
        <path d="M116 98 C128 74 146 88 154 64" strokeDasharray="4 7" />
        <path
          className="home-pic__fill"
          d="M156 66 C150 58 146 54 146 49 A10 10 0 0 1 166 49 C166 54 162 58 156 66 Z"
        />
      </Picture>
    ),
  },
];

export const EVERYONE_TILES: readonly TileCopy[] = [
  {
    id: 'captain',
    title: 'The captain',
    text: 'A second set of eyes on every system, with clear explanations when something starts to drift.',
    picture: (
      <Picture>
        <path d="M100 18V122M48 70H152M63 33L137 107M137 33L63 107" />
        <circle cx="100" cy="70" r="36" />
        <circle cx="100" cy="18" r="5" />
        <circle cx="100" cy="122" r="5" />
        <circle cx="48" cy="70" r="5" />
        <circle cx="152" cy="70" r="5" />
        <circle cx="63" cy="33" r="5" />
        <circle cx="137" cy="107" r="5" />
        <circle cx="137" cy="33" r="5" />
        <circle cx="63" cy="107" r="5" />
        <circle className="home-pic__fill" cx="100" cy="70" r="12" />
      </Picture>
    ),
  },
  {
    id: 'family',
    title: 'Spouses & family',
    text: 'Confidence to share the work of running the boat, without having to learn every gauge first.',
    picture: (
      <Picture>
        <path d="M24 116H176" />
        <path d="M28 116 Q40 130 60 130 H140 Q160 130 172 116" />
        <circle cx="68" cy="46" r="12" />
        <path d="M48 112 Q48 72 68 72 Q88 72 88 112" />
        <circle cx="134" cy="42" r="13" />
        <path d="M112 112 Q112 70 134 70 Q156 70 156 112" />
        <circle className="home-pic__fill" cx="101" cy="72" r="9" />
        <path d="M88 112 Q88 88 101 88 Q114 88 114 112" />
      </Picture>
    ),
  },
  {
    id: 'guests',
    title: 'Guests & Help',
    text: 'Friendly answers about water, power and plans — so nobody has to interrupt the captain.',
    picture: (
      <Picture>
        <rect x="28" y="24" width="98" height="58" rx="14" />
        <path d="M50 82 L44 100 L66 82" />
        <path d="M46 44H108M46 60H90" />
        <rect className="home-pic__fill" x="86" y="60" width="88" height="50" rx="14" />
        <path className="home-pic__fill" d="M150 110 L158 126 L136 110" />
        <circle className="home-pic__ink" cx="112" cy="85" r="4" />
        <circle className="home-pic__ink" cx="130" cy="85" r="4" />
        <circle className="home-pic__ink" cx="148" cy="85" r="4" />
      </Picture>
    ),
  },
  {
    id: 'ashore',
    title: 'Owners ashore',
    text: "Got an alert while you're away? Open a conversation and ask Neo what triggered it and whether to act.",
    picture: (
      <Picture>
        <rect x="72" y="14" width="56" height="112" rx="10" />
        <path d="M92 114H108" />
        <path d="M84 72 H116 L110 82 H90 Z" />
        <path d="M94 72 V63 H106 V72" />
        <path d="M80 92 q5 -4 10 0 t10 0 t10 0 t10 0" />
        <circle className="home-pic__fill" cx="130" cy="22" r="14" />
        <path className="home-pic__ink-line" d="M130 15 V24" />
        <circle className="home-pic__ink home-pic__ink-line" cx="130" cy="30" r="1.6" />
      </Picture>
    ),
  },
];

export const HOW_TILES: readonly TileCopy[] = [
  {
    id: 'connect',
    kicker: 'STEP 1',
    title: 'Connect',
    text: "Neomaris Gateway or a compatible software interface links Neo to your boat's existing NMEA 2000 network — Garmin, Furuno, Raymarine, Simrad and more.",
    picture: (
      <Picture>
        <path d="M44 36 L82 58M156 36 L118 58M44 104 L82 82M156 104 L118 82" />
        <circle cx="36" cy="30" r="11" />
        <circle cx="164" cy="30" r="11" />
        <circle cx="36" cy="110" r="11" />
        <circle cx="164" cy="110" r="11" />
        <rect className="home-pic__fill" x="78" y="50" width="44" height="40" rx="8" />
        <path className="home-pic__ink-line" d="M92 70 H108M100 62 V78" />
      </Picture>
    ),
  },
  {
    id: 'learn',
    kicker: 'STEP 2',
    title: 'Learn',
    text: "Neo learns your boat's normal operating conditions and draws on your manuals, maintenance records and voyage history.",
    picture: (
      <Picture>
        <rect className="home-pic__band" x="30" y="58" width="150" height="28" />
        <path d="M30 20 V118 H180" />
        <path d="M30 76 L55 70 L80 78 L105 68 L130 74 L150 72 L170 38" />
        <circle className="home-pic__fill" cx="170" cy="38" r="7" />
      </Picture>
    ),
  },
  {
    id: 'ask',
    kicker: 'STEP 3',
    title: 'Ask',
    text: 'Talk to Neo from the mobile or browser app, aboard or ashore. Basic local alarms and data caching keep working if you lose connection.',
    picture: (
      <Picture>
        <rect x="22" y="32" width="102" height="66" rx="6" />
        <path d="M10 106 H136" />
        <rect className="home-pic__fill" x="38" y="48" width="56" height="20" rx="8" />
        <path d="M60 80 H104" />
        <rect className="home-pic__face" x="138" y="44" width="42" height="78" rx="8" />
        <path d="M148 64 H170M148 76 H162M154 112 H164" />
      </Picture>
    ),
  },
];
